'use client';

import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowRight, Clock3, Radio, Save, Ticket, Calendar } from 'lucide-react';
import { useAuthStore } from '@/lib/auth-store';
import { getEffectiveRole, isOrganizerRole, isOrganizationAdminRole } from '@/lib/rbac';
import { canUseOrganizerDashboardApis } from '@/lib/organizer-access';
import { eventDiscoveryApi, type EventCategoryItem } from '@/lib/event-discovery-api';
import { DynamicIconRenderer } from '@/components/events-discovery/DynamicIconRenderer';

interface EventMegaMenuProps {
  onClose?: () => void;
}

export function EventMegaMenu({ onClose }: EventMegaMenuProps) {
  const { isAuthenticated, user } = useAuthStore();
  const [categories, setCategories] = React.useState<EventCategoryItem[]>([]);
  const [eventSectionCounts, setEventSectionCounts] = React.useState({ drafts: 0, live: 0, past: 0 });

  const effectiveRole = getEffectiveRole(user);
  const isOrganizerUser = Boolean(
    user && (
      isOrganizerRole(effectiveRole) ||
      canUseOrganizerDashboardApis(user) ||
      isOrganizationAdminRole(effectiveRole)
    )
  );

  React.useEffect(() => {
    eventDiscoveryApi
      .categories()
      .then((response) => setCategories(response.data.data ?? []))
      .catch(() => setCategories([]));
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    Promise.all([
      eventDiscoveryApi.myEventSections({ tab: 'saved_drafts', per_page: 1 }),
      eventDiscoveryApi.myEventSections({ tab: 'live_events', per_page: 1 }),
      eventDiscoveryApi.myEventSections({ tab: 'past_events', per_page: 1 }),
    ])
      .then(([draftsRes, liveRes, pastRes]) => {
        setEventSectionCounts({
          drafts: draftsRes.data.meta?.total ?? 0,
          live: liveRes.data.meta?.total ?? 0,
          past: pastRes.data.meta?.total ?? 0,
        });
      })
      .catch(() => {
        setEventSectionCounts({ drafts: 0, live: 0, past: 0 });
      });
  }, [isAuthenticated]);

  const myEventsHref = isAuthenticated
    ? (isOrganizerUser ? '/dashboard/events' : '/dashboard/my-events')
    : '/login?redirect=/dashboard/events';

  return (
    <div className="p-5 w-[44rem] max-w-[88vw] max-h-[min(78vh,550px)] overflow-y-auto bg-white rounded-2xl">
      {/* ── My Events Section Header & Shortcuts ── */}
      <div className="mb-5 rounded-2xl border border-nature-border bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <Link
            href={myEventsHref}
            onClick={onClose}
            className="group inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-moss hover:text-soot transition-colors"
          >
            <span>{isAuthenticated ? (isOrganizerUser ? 'My Organizer Events' : 'My Events & Tickets') : 'My Events'}</span>
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
          </Link>
          {!isAuthenticated && (
            <Link
              href="/login?redirect=/dashboard/events"
              onClick={onClose}
              className="text-xs font-medium text-moss hover:underline"
            >
              Sign in
            </Link>
          )}
        </div>

        <div className="mt-3 grid grid-cols-1 gap-2.5 md:grid-cols-3">
          {isOrganizerUser ? (
            <>
              <Link
                href="/dashboard/events?filter=draft"
                onClick={onClose}
                className="flex items-center gap-2.5 rounded-xl border border-nature-border bg-white px-3.5 py-2.5 text-sm font-semibold text-soot transition-all hover:border-moss hover:bg-mist/10 hover:shadow-xs"
              >
                <Save className="h-4 w-4 shrink-0 text-moss" />
                <span className="truncate">Saved Drafts ({eventSectionCounts.drafts})</span>
              </Link>
              <Link
                href="/dashboard/events?filter=active"
                onClick={onClose}
                className="flex items-center gap-2.5 rounded-xl border border-nature-border bg-white px-3.5 py-2.5 text-sm font-semibold text-soot transition-all hover:border-moss hover:bg-mist/10 hover:shadow-xs"
              >
                <Radio className="h-4 w-4 shrink-0 text-moss" />
                <span className="truncate">Live Events ({eventSectionCounts.live})</span>
              </Link>
              <Link
                href="/dashboard/events?filter=past"
                onClick={onClose}
                className="flex items-center gap-2.5 rounded-xl border border-nature-border bg-white px-3.5 py-2.5 text-sm font-semibold text-soot transition-all hover:border-moss hover:bg-mist/10 hover:shadow-xs"
              >
                <Clock3 className="h-4 w-4 shrink-0 text-moss" />
                <span className="truncate">Past Events ({eventSectionCounts.past})</span>
              </Link>
            </>
          ) : isAuthenticated ? (
            <>
              <Link
                href="/dashboard/my-tickets"
                onClick={onClose}
                className="flex items-center gap-2.5 rounded-xl border border-nature-border bg-white px-3.5 py-2.5 text-sm font-semibold text-soot transition-all hover:border-moss hover:bg-mist/10 hover:shadow-xs"
              >
                <Ticket className="h-4 w-4 shrink-0 text-moss" />
                <span className="truncate">My Tickets</span>
              </Link>
              <Link
                href="/dashboard/my-events?tab=live"
                onClick={onClose}
                className="flex items-center gap-2.5 rounded-xl border border-nature-border bg-white px-3.5 py-2.5 text-sm font-semibold text-soot transition-all hover:border-moss hover:bg-mist/10 hover:shadow-xs"
              >
                <Radio className="h-4 w-4 shrink-0 text-moss" />
                <span className="truncate">Live Events</span>
              </Link>
              <Link
                href="/dashboard/my-events?tab=past"
                onClick={onClose}
                className="flex items-center gap-2.5 rounded-xl border border-nature-border bg-white px-3.5 py-2.5 text-sm font-semibold text-soot transition-all hover:border-moss hover:bg-mist/10 hover:shadow-xs"
              >
                <Clock3 className="h-4 w-4 shrink-0 text-moss" />
                <span className="truncate">Past Events</span>
              </Link>
            </>
          ) : (
            <>
              <Link
                href="/events"
                onClick={onClose}
                className="flex items-center gap-2.5 rounded-xl border border-nature-border bg-white px-3.5 py-2.5 text-sm font-semibold text-soot transition-all hover:border-moss hover:bg-mist/10 hover:shadow-xs"
              >
                <Calendar className="h-4 w-4 shrink-0 text-moss" />
                <span className="truncate">All Events</span>
              </Link>
              <Link
                href="/events/calendar"
                onClick={onClose}
                className="flex items-center gap-2.5 rounded-xl border border-nature-border bg-white px-3.5 py-2.5 text-sm font-semibold text-soot transition-all hover:border-moss hover:bg-mist/10 hover:shadow-xs"
              >
                <Clock3 className="h-4 w-4 shrink-0 text-moss" />
                <span className="truncate">Event Calendar</span>
              </Link>
              <Link
                href="/host-events"
                onClick={onClose}
                className="flex items-center gap-2.5 rounded-xl border border-nature-border bg-white px-3.5 py-2.5 text-sm font-semibold text-soot transition-all hover:border-moss hover:bg-mist/10 hover:shadow-xs"
              >
                <Radio className="h-4 w-4 shrink-0 text-moss" />
                <span className="truncate">Host an Event</span>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* ── Categories Section ── */}
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-wider text-moss">
          Browse Categories
        </h3>
        <Link
          href="/events"
          onClick={onClose}
          className="text-xs font-semibold text-moss hover:underline"
        >
          View All Events →
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {categories.map((category, index) => (
          <motion.div
            key={category.slug}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15, delay: index * 0.03 }}
          >
            <Link
              href={`/events?type=${encodeURIComponent(category.slug)}`}
              onClick={onClose}
              className="group relative flex items-start gap-3.5 p-3.5 rounded-xl border border-nature-border bg-white transition-all duration-200 hover:border-moss hover:shadow-sm"
            >
              <div className="shrink-0 w-11 h-11 rounded-lg flex items-center justify-center bg-mist/30 border border-nature-border/60 transition-colors group-hover:bg-eucalyptus/20">
                <DynamicIconRenderer iconName={category.icon_name} iconType={category.icon_type} className="w-5 h-5 text-moss" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-sm font-bold text-soot group-hover:text-moss transition-colors">
                    {category.name}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 text-moss" />
                </div>
                <p className="text-xs text-moss/80 line-clamp-2 leading-relaxed">
                  {category.short_description || 'Explore curated events from this category.'}
                </p>
              </div>
            </Link>
          </motion.div>
        ))}
        {categories.length === 0 && (
          <div className="rounded-xl border border-nature-border bg-white p-4 text-sm text-moss">
            Categories are being updated. Please check back shortly.
          </div>
        )}
      </div>

      <div className="mt-4 pt-3.5 border-t border-nature-border flex items-center justify-between">
        <p className="text-xs text-moss">
          Browse by category or pick a template to host your own event.
        </p>
        <Link 
          href="/events" 
          onClick={onClose}
          className="text-xs font-bold text-moss hover:underline flex items-center gap-1"
        >
          Explore All Events
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
